import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { User, Users, UserPlus, Building2, ScrollText, ArrowUpRight, Gauge } from "lucide-react";

function HubCard({ to, icon: Icon, eyebrow, title, description, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block border border-neutral-200 bg-white p-6 hover:border-neutral-900 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between mb-6">
        <div className="w-10 h-10 bg-neutral-900 grid place-items-center group-hover:scale-105 transition-transform">
          <Icon size={18} className="text-white" />
        </div>
        <ArrowUpRight size={16} className="text-neutral-400 group-hover:text-neutral-900 transition-colors" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">{eyebrow}</div>
      <div className="font-display text-lg font-bold tracking-tight mb-1">{title}</div>
      <div className="text-xs text-neutral-500 leading-relaxed">{description}</div>
    </Link>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Account"
        title="Settings"
        description="Profile, organization, and audit controls — all in one place."
        testid="settings-header"
      />

      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          <div className="bg-white p-6" data-testid="settings-profile-card">
            <div className="flex items-start justify-between mb-6">
              <div className="w-10 h-10 bg-neutral-900 grid place-items-center">
                <User size={18} className="text-white" />
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">Profile</div>
            <div className="font-display text-lg font-bold tracking-tight mb-4">{user?.name || "—"}</div>
            <div className="space-y-2 text-sm">
              <Row k="Email" v={user?.email} />
              <Row k="Role" v={user?.role?.replace("_", " ")} />
              <Row k="Capacity" v={`${user?.weekly_capacity_hours ?? 40}h / week`} />
              <Row k="Member since" v={user?.created_at?.slice(0, 10)} />
            </div>
          </div>

          {isAdmin && (
            <HubCard
              to="/team"
              icon={UserPlus}
              eyebrow="Organization"
              title="Team"
              description="Invite teammates, change roles, set weekly capacity, bulk-reassign work, and see utilization."
              testid="settings-team-card"
            />
          )}
          <HubCard
            to="/clients"
            icon={Building2}
            eyebrow="CRM"
            title="Clients"
            description="Master client list. Add or update contact info, status, and notes per client."
            testid="settings-clients-card"
          />
          <HubCard
            to="/settings/benchmarks"
            icon={Gauge}
            eyebrow="Standards"
            title="Task benchmarks"
            description="Define expected hours per unit for the work types you do often. Powers project efficiency reports."
            testid="settings-benchmarks-card"
          />
          {isAdmin && (
            <HubCard
              to="/audit"
              icon={ScrollText}
              eyebrow="Compliance"
              title="Audit log"
              description="Time-stamped trail of every consequential action — role changes, billing updates, archives."
              testid="settings-audit-card"
            />
          )}
        </div>
      </Section>

      <Section>
        <div className="border border-neutral-200 bg-neutral-50 p-5 max-w-2xl">
          <div className="flex items-start gap-3">
            <div className="w-1 h-full bg-neutral-900 self-stretch" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">Tip</div>
              <div className="text-sm text-neutral-700 leading-relaxed">
                Need to change your password? Sign out and use the activation link your{" "}
                {isSuperAdmin ? "co-super-admin" : "admin"} can resend from the Team page.
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-100 pb-2">
      <span className="text-neutral-500">{k}</span>
      <span className="font-medium">{v || "—"}</span>
    </div>
  );
}
